
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import vSelect from 'vue-select';
import Popup from '@/components/utils/Popup.vue';
import AvatarSettings from '@/components/home/AvatarSettings.vue';
import {
  DropdownOption,
  NotificationModes,
  SettingsSnapshot,
  CustomModes,
  User,
  GeneralSettingsSnapshot,
  UserProperties,
} from '@/utils/models';
import { DEVICE_CANNOT_ACQUIRE_NO_JS_FUNCTIONS } from '@/utils/constants';
import { findDeviceOnDevicesList, printMediaDevicesState } from '@/utils/functions';
import { JitsiLib } from '@/service/JitsiLibWrapper';
import store from 'store';
import PasswordChangePopup from './PasswordChangePopup.vue';
import MicTest from './MicTest.vue';
import SpeakerTest from './SpeakerTest.vue';

@Component({ components: { Popup, AvatarSettings, 'v-select': vSelect, PasswordChangePopup, MicTest, SpeakerTest } })
export default class GeneralSettingsPopup extends Vue {
  private isAvatarBeingUploaded = false;
  availableVideos: Array<DropdownOption> = [];
  availableAudios: Array<DropdownOption> = [];
  availableOutputs: Array<DropdownOption> = [];
  private devices: { [deviceId: string]: MediaDeviceInfo } = {};
  selectedVideo: DropdownOption = {
    id: 'none',
    label: 'No available video devices',
  };
  selectedAudio: DropdownOption = {
    id: 'none',
    label: 'No available audio devices',
  };
  selectedOutput: DropdownOption = {
    id: 'none',
    label: 'No available output devices',
  };
  avatarBase64: string = '';
  avatarShape: 'circle' | 'rectangle' = 'circle';
  displayName: string = '';
  voiceAreaColor: string = '';
  enableNoiseSuppressor: boolean = true;
  enableShortcut: boolean = false;
  get hasSettingChanged(): boolean {
    return (
      this.defaultAvatarBase64 !== this.avatarBase64 ||
      this.defaultAvatarShape !== this.avatarShape ||
      this.userName !== this.displayName.trim() ||
      this.selectedVoiceAreaColor !== this.voiceAreaColor ||
      this.defaultVideoInputDeviceId !== this.selectedVideo.id ||
      this.defaultAudioInputDeviceId !== this.selectedAudio.id ||
      this.defaultOutputDeviceId !== this.selectedOutput.id ||
      this.defaultNotificationMode !== this.selectedNotificationMode ||
      this.defaultProximityNotificationMode !== this.selectedProximityNotificationMode ||
      this.defaultMessageNotificationMode !== this.selectedMessageNotificationMode ||
      this.enableNoiseSuppressor !== this.defaultEnableNoiseSuppressor ||
      this.enableShortcut !== this.defaultEnableShortcut ||
      this.selectedCustomMode.sort().toString() !== this.defaultCustomMode.sort().toString()
    );
  }
  get isB2c(): boolean {
    const user = this.$store.state.user as User;
    if (!user) {
      return false;
    }
    return user.authType === 'aad-b2c';
  }

  get isB2cForTrial(): boolean {
    const user = this.$store.state.user as User;
    if (!user) {
      return false;
    }
    return user.authType === 'b2c-demo';
  }

  private get defaultVideoInputDeviceId(): string | undefined {
    return store.get('chosenVideoInputDeviceId');
  }
  private get defaultAudioInputDeviceId(): string {
    return store.get('chosenAudioInputDeviceId');
  }
  private get defaultOutputDeviceId(): string {
    return store.get('chosenAudioOutputDeviceId');
  }
  private get defaultEnableNoiseSuppressor(): boolean {
    let properties: UserProperties | undefined = this.$store.state.userProperties;
    return properties?.enableNoiseSuppressor ?? true;
  }
  get defaultEnableShortcut(): boolean {
    let properties: UserProperties | undefined = this.$store.state.userProperties;
    return properties?.enableShortcut ?? false;
  }
  get name(): string {
    let user = this.$store.state.user as User;
    if (!user) {
      return '';
    }
    return user.name;
  }
  get userName(): string {
    let user = this.$store.state.user as User;
    if (!user) {
      return '';
    }
    return user.displayName ?? user.name;
  }
  get selectedVoiceAreaColor(): string {
    let user = this.$store.state.user as User;
    return user?.voiceAreaColor || 'pink';
  }
  get authType(): string {
    return (this.$store.state.user as User)?.authType || '';
  }
  private get defaultAvatarBase64(): string {
    return this.$store.state.user.avatarBase64 || '';
  }
  private get defaultAvatarShape(): 'circle' | 'rectangle' {
    return this.$store.state.user.avatarShape || 'circle';
  }
  private get defaultNotificationMode(): NotificationModes {
    let properties: UserProperties = this.$store.state.userProperties;
    let mode = properties.selectedNotificationMode;
    return mode;
  }
  private get defaultProximityNotificationMode(): NotificationModes {
    let properties: UserProperties = this.$store.state.userProperties;
    let mode = properties.selectedProximityNotificationMode;
    return mode;
  }
  private get defaultMessageNotificationMode(): NotificationModes {
    let properties: UserProperties = this.$store.state.userProperties;
    let mode = properties.selectedMessageNotificationMode;
    return mode;
  }
  private get defaultCustomMode(): CustomModes {
    let properties: UserProperties = this.$store.state.userProperties;
    let mode = properties.selectedCustomModes;
    return (typeof mode === 'string' ? [mode] : mode) || [];
  }
  get isCustomSelected(): boolean {
    return this.selectedNotificationMode === 'Custom';
  }
  get canUpdateDisplayName(): boolean {
    return this.displayName.trim() !== this.userName;
  }
  selectedNotificationMode: NotificationModes = this.defaultNotificationMode || 'ALL';
  selectedProximityNotificationMode: NotificationModes = this.defaultProximityNotificationMode || 'ALL';
  selectedMessageNotificationMode: NotificationModes = this.defaultMessageNotificationMode || 'ALL';
  // private selectedCustomMode: CustomModes = this.defaultCustomMode || "Mylist";
  selectedCustomMode: CustomModes = this.defaultCustomMode || [];

  showPasswordChange = false;
  mounted() {
    JitsiLib.mediaDevices.addEventListener(JitsiLib.events.mediaDevices.DEVICE_LIST_CHANGED, this.onDeviceListChanged);
    this.avatarBase64 = this.defaultAvatarBase64;
    this.avatarShape = this.defaultAvatarShape;
    this.displayName = this.userName;
    this.voiceAreaColor = this.selectedVoiceAreaColor;
    this.enableNoiseSuppressor = this.defaultEnableNoiseSuppressor;
    this.enableShortcut = this.defaultEnableShortcut;
    this.fillInAvailableDeviceList();
  }
  beforeDestroy() {
    JitsiLib.mediaDevices.removeEventListener(
      JitsiLib.events.mediaDevices.DEVICE_LIST_CHANGED,
      this.onDeviceListChanged,
    );
  }
  @Emit()
  showMediaDeviceErrorPopup(message: string) {
    return message;
  }
  openProfileChangeDialog() {
    if (!(this.isB2c || this.isB2cForTrial)) return;
    const authType = this.$store.state.user.authType as string;
    this.$toast.info('変更した値は次のログイン時に反映されます。');
    const windowFeatures = 'menubar=no,scrollbars=yes,status=no';
    if (authType === 'aad-b2c') {
      window.open('/zawa-data/aad-b2c/profile', 'profile', windowFeatures);
    } else if (authType === 'b2c-demo') {
      window.open('/zawa-data/b2c-demo/profile', 'profile', windowFeatures);
    }
  }

  openPasswordChangeDialog() {
    const authType = this.$store.state.user.authType as string;
    const windowFeatures = 'menubar=no,scrollbars=yes,status=no';
    if (authType === 'aad-b2c') {
      window.open('/zawa-data/aad-b2c/password', 'password', windowFeatures);
    } else if (authType === 'b2c-demo') {
      window.open('/zawa-data/b2c-demo/password', 'password', windowFeatures);
    }
    (window as any).onPasswordChanged = () => {
      // パスワード変更後はログインもう一度ログインさせたいので
      this.$toast.info('パスワードが変更されたため、再度ログインをしてください。3秒後にリダイレクトします...');
      setTimeout(() => {
        location.href = '/';
      }, 3000);
    };
  }

  private onDeviceListChanged(devices: Array<MediaDeviceInfo>): void {
    this.fillInAvailableDeviceList();
  }

  private fillInAvailableDeviceList(): void {
    const retrievedMediaDevices: Array<MediaDeviceInfo> = [];
    const isHTTPsOrLocalhost = location.protocol === 'https:' || location.host.startsWith('localhost');

    let canEnumerate = false;
    let hasMicrophone = false;
    let hasSpeakers = false;
    let hasWebcam = false;
    let isMicrophoneAlreadyCaptured = false;
    let isWebcamAlreadyCaptured = false;
    let isInvokeGetUserMediaNeeded = false;

    if (typeof MediaStreamTrack !== 'undefined' && 'getSources' in MediaStreamTrack) {
      canEnumerate = true;
    } else if (navigator.mediaDevices && !!navigator.mediaDevices.enumerateDevices) {
      canEnumerate = true;
    }

    if (!canEnumerate) {
      console.error('Getting media devices impossible - no getSources and navigator.mediaDevices.enumerateDevices');
      this.showMediaDeviceErrorPopup(DEVICE_CANNOT_ACQUIRE_NO_JS_FUNCTIONS);
      return;
    }

    if (
      !navigator.mediaDevices.enumerateDevices &&
      window.MediaStreamTrack &&
      (window.MediaStreamTrack as any).getSources
    ) {
      navigator.mediaDevices.enumerateDevices = (window.MediaStreamTrack as any).getSources.bind(
        window.MediaStreamTrack,
      );
    }

    if (!navigator.mediaDevices.enumerateDevices) {
      console.error('No enumerateDevices');
      this.showMediaDeviceErrorPopup(DEVICE_CANNOT_ACQUIRE_NO_JS_FUNCTIONS);
      printMediaDevicesState(
        hasWebcam,
        hasMicrophone,
        hasSpeakers,
        isMicrophoneAlreadyCaptured,
        isWebcamAlreadyCaptured,
      );
      return;
    }

    navigator.mediaDevices.enumerateDevices().then((devices: Array<MediaDeviceInfo>) => {
      devices.forEach((_device) => {
        if (!_device.label) {
          isInvokeGetUserMediaNeeded = true;
          return;
        }

        const device = _device;
        if (!device.label) {
          if (!isHTTPsOrLocalhost) {
            console.error('HTTPs required to get access to media device.');
          }
        } else {
          if (device.kind === 'videoinput') {
          }

          if (device.kind === 'audioinput') {
          }
        }
        switch (device.kind) {
          case 'audioinput':
            isMicrophoneAlreadyCaptured = true;
            hasMicrophone = true;
            break;
          case 'audiooutput':
            hasSpeakers = true;
            break;
          case 'videoinput':
            hasWebcam = true;
            isWebcamAlreadyCaptured = true;
            break;
        }
        retrievedMediaDevices.push(device);
      });

      printMediaDevicesState(
        hasWebcam,
        hasMicrophone,
        hasSpeakers,
        isMicrophoneAlreadyCaptured,
        isWebcamAlreadyCaptured,
      );
      //   this.setDeviceAvailability(hasWebcam, hasMicrophone);

      // return isInvokeGetUserMediaNeeded;

      if (isInvokeGetUserMediaNeeded) {
        console.log('Invoking navigator.mediaDevices.getUserMedia');
        navigator.mediaDevices
          .getUserMedia({ audio: true, video: true })
          .catch((err: any) => {
            console.error('Error on invoking getUserMedia', err);
          })
          .finally(() => {
            this.groupDevices();
          });
      } else {
        this.groupDevices();
      }
    });
  }
  private groupDevices(): void {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      console.log('navigator devices', devices);
    });

    JitsiLib.mediaDevices.enumerateDevices((devices: Array<MediaDeviceInfo>) => {
      console.log('jitsi devices', devices);
      this.resetDevicesLists();

      devices.forEach((d: MediaDeviceInfo) => {
        this.devices[d.deviceId] = d;

        if (d.kind === 'audioinput') {
          this.availableAudios.push({ id: d.deviceId, label: d.label });
        } else if (d.kind === 'videoinput') {
          this.availableVideos.push({ id: d.deviceId, label: d.label });
        } else if (d.kind === 'audiooutput') {
          this.availableOutputs.push({ id: d.deviceId, label: d.label });
        }
      });

      this.selectedAudio = this.getSelectedDevice(
        this.availableAudios,
        this.defaultAudioInputDeviceId,
        this.selectedAudio,
      );
      this.selectedVideo = this.getSelectedDevice(
        this.availableVideos,
        this.defaultVideoInputDeviceId,
        this.selectedVideo,
      );
      this.selectedOutput = this.getSelectedDevice(
        this.availableOutputs,
        this.defaultOutputDeviceId,
        this.selectedOutput,
      );
    });
  }

  private getSelectedDevice(
    availableDevices: Array<DropdownOption>,
    defaultDeviceId: string | undefined,
    selectedDevice: DropdownOption,
  ): DropdownOption {
    const selectedDeviceOnAvailable = findDeviceOnDevicesList(availableDevices, selectedDevice.id);
    if (selectedDeviceOnAvailable) {
      return selectedDeviceOnAvailable;
    } else {
      const defaultDevice = findDeviceOnDevicesList(availableDevices, defaultDeviceId);

      if (!defaultDevice || defaultDeviceId === undefined) {
        return availableDevices.filter((v) => v.id).length > 0
          ? ({
              id: availableDevices[0].id,
              label: availableDevices[0].label,
            } as DropdownOption)
          : {
              id: 'none',
              label: 'No available devices',
            };
      } else {
        if (defaultDevice) {
          return defaultDevice;
        }
        return {
          id: this.devices[defaultDeviceId].deviceId,
          label: this.devices[defaultDeviceId].label,
        } as DropdownOption;
      }
    }
  }

  private resetDevicesLists(): void {
    this.availableAudios = [];
    this.availableVideos = [];
    this.availableOutputs = [];
  }
  public hideImageLoader(checkIfError: boolean): void {
    if (checkIfError && this.$refs.avatarSettingsRef) {
      const isError = (this.$refs.avatarSettingsRef as AvatarSettings).isSmallLoaderVisible;
      if (isError) {
        // this.isErrorOnAvatarUpload = true;

        if (this.isAvatarBeingUploaded) {
          this.isAvatarBeingUploaded = false;
        }
      } else if (this.isAvatarBeingUploaded) {
        this.isAvatarBeingUploaded = false;
        this.applyNewSettings();
      }
    }
    if (this.$refs.avatarSettingsRef) {
      (this.$refs.avatarSettingsRef as AvatarSettings).isSmallLoaderVisible = false;
    }
  }
  onApplySettingsButtonClick(): void {
    if (!this.hasSettingChanged) {
      return;
    }
    this.applyNewSettings();
  }
  onApplyAvatar(val: { avatar: string; avatarShape: 'circle' | 'rectangle' }) {
    this.avatarBase64 = val.avatar;
    this.avatarShape = val.avatarShape;
  }
  updateDisplayName() {}
  @Emit()
  private applyNewSettings(): GeneralSettingsSnapshot {
    let r: SettingsSnapshot = {
      selectedVideoId: this.selectedVideo.id,
      selectedAudioId: this.selectedAudio.id,
      selectedOutputId: this.selectedOutput.id,
      displayName: this.displayName,
      avatarBase64: this.avatarBase64,
      avatarShape: this.avatarShape,
      voiceAreaColor: this.voiceAreaColor,
    };
    return {
      settings: r,
      properties: {
        selectedNotificationMode: this.selectedNotificationMode,
        selectedProximityNotificationMode: this.selectedProximityNotificationMode,
        selectedMessageNotificationMode: this.selectedMessageNotificationMode,
        selectedCustomModes: this.selectedCustomMode,
        enableNoiseSuppressor: this.enableNoiseSuppressor,
        enableShortcut: this.enableShortcut,
      },
    };
  }
  @Emit()
  closeSettingsWithoutSave() {}
}
